import { motion } from "framer-motion";
import {
  Equals,
  ArrowsOutSimple,
  Stack,
  Swap,
  Function as FnIcon,
  Infinity as InfinityIcon,
  Target,
} from "@phosphor-icons/react";
import { cn } from "../../lib/cn";
import type { TransformType } from "../../stores/equationPlayground";
import type { Icon as PhosphorIcon } from "@phosphor-icons/react";

interface TransformPaletteProps {
  onTransform: (transform: TransformType) => void;
  isTransforming: boolean;
  className?: string;
}

interface TransformOption {
  id: TransformType;
  label: string;
  icon: PhosphorIcon;
  description: string;
}

const TRANSFORMS: TransformOption[] = [
  { id: "simplify", label: "Simplify", icon: Equals, description: "Reduce to simplest form" },
  { id: "expand", label: "Expand", icon: ArrowsOutSimple, description: "Expand all products" },
  { id: "factor", label: "Factor", icon: Stack, description: "Factor into components" },
  { id: "substitute", label: "Substitute", icon: Swap, description: "Replace a variable" },
  { id: "differentiate", label: "d/dx", icon: FnIcon, description: "Compute derivative" },
  { id: "integrate", label: "∫ dx", icon: InfinityIcon, description: "Compute integral" },
  { id: "isolate", label: "Isolate", icon: Target, description: "Solve for a variable" },
];

const spring = { type: "spring" as const, stiffness: 400, damping: 25 };

function TransformPalette({ onTransform, isTransforming, className }: TransformPaletteProps) {
  return (
    <div className={cn("grid grid-cols-4 gap-1.5", className)}>
      {TRANSFORMS.map((t) => {
        const Icon = t.icon;
        return (
          <motion.button
            key={t.id}
            onClick={() => onTransform(t.id)}
            disabled={isTransforming}
            whileHover={{ scale: 1.04, backgroundColor: "var(--bg-surface-hover)" }}
            whileTap={{ scale: 0.96 }}
            transition={spring}
            className={cn(
              "flex flex-col items-center gap-1 rounded-[var(--radius-md)] px-2 py-2",
              "text-[var(--text-secondary)] hover:text-[var(--text-primary)]",
              "border border-transparent hover:border-[var(--border-hairline)]",
              "transition-colors duration-[var(--duration-fast)]",
              "disabled:opacity-40 disabled:pointer-events-none",
            )}
            title={t.description}
          >
            <Icon size={16} weight="bold" />
            <span className="text-[10px] font-medium">{t.label}</span>
          </motion.button>
        );
      })}
    </div>
  );
}

TransformPalette.displayName = "TransformPalette";

export { TransformPalette };
export type { TransformPaletteProps };
